import React, { useState, useMemo } from 'react';
import {
  Popover,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { Box, Button, Typography, Divider } from '@common-ui-library/atom';
import { useI18n } from '@common-ui-library/shared';
import { EntityFilterConfig, FilterOption } from '../Props/entityFilterConfigs';
import { DropdownOptions } from '../Store/useUserManagementStore';

interface FilterPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  config: EntityFilterConfig;
  dropdownOptions: DropdownOptions | null;
  onApply: (filters: string[]) => void;
  currentFilters: string[];
}

interface FilterValues {
  [key: string]: string;
}

const FilterPopover: React.FC<FilterPopoverProps> = ({
  anchorEl,
  open,
  onClose,
  config,
  dropdownOptions,
  onApply,
  currentFilters,
}) => {
  const { t } = useI18n();

  // Parse current filters into key-value pairs
  const initialValues = useMemo(() => {
    const values: FilterValues = {};
    currentFilters.forEach((filter) => {
      const [key, value] = filter.split('=');
      if (key && value) {
        values[key] = value;
      }
    });
    return values;
  }, [currentFilters]);

  const [filterValues, setFilterValues] = useState<FilterValues>(initialValues);

  // Reset local state when popover opens with new filters
  React.useEffect(() => {
    if (open) {
      setFilterValues(initialValues);
    }
  }, [open, initialValues]);

  const handleFilterChange = (key: string, value: string) => {
    setFilterValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApply = () => {
    // Convert filter values to string array format: ['key=value', ...]
    const filters = Object.entries(filterValues)
      .filter(([_, value]) => value && value !== '' && value !== 'all')
      .map(([key, value]) => `${key}=${value}`);
    onApply(filters);
    onClose();
  };

  const handleClear = () => {
    setFilterValues({});
    onApply([]);
    onClose();
  };

  const getOptionsForFilter = (filter: FilterOption): { value: string; label: string }[] => {
    if (filter.staticOptions) {
      return filter.staticOptions;
    }
    
    const options = dropdownOptions?.[filter.optionsKey ?? ''];
    if (!Array.isArray(options)) {
      return [];
    }

    return options.map((opt: any) => ({
      value: filter.valueKey ? opt[filter.valueKey] : opt.value,
      label: opt.label ?? opt.value,
    }));
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      <Box sx={{ p: 2, minWidth: 280 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          {t('labels.filters')}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {config.filters.map((filter) => {
          const options = getOptionsForFilter(filter);
          return (
            <FormControl key={filter.key} fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>{filter.label}</InputLabel>
              <Select
                value={filterValues[filter.key] || ''}
                label={filter.label}
                onChange={(e: SelectChangeEvent) =>
                  handleFilterChange(filter.key, e.target.value)
                }
              >
                <MenuItem value="">
                  <em>{t('labels.none')}</em>
                </MenuItem>
                {options.map((opt, index) => (
                    <MenuItem key={opt.value ?? `option-${index}`} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        })}

        <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={handleClear} size="small">
            {t('buttons.clear')}
          </Button>
          <Button variant="contained" onClick={handleApply} size="small">
            {t('buttons.apply')}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default FilterPopover;

